"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { usePathname, useRouter } from "next/navigation";

import {
  GAM_ENABLED,
  GAM_INTERSTITIAL_AD_UNIT,
  GAM_INTERSTITIAL_FALLBACK_AD_UNIT,
  GAM_INTERSTITIAL_FALLBACK_SIZES,
  INTERSTITIAL_COOLDOWN_MS,
  INTERSTITIAL_MIN_NAVIGATIONS,
  INTERSTITIAL_REPEAT_NAVIGATIONS,
  INTERSTITIAL_REQUEST_TIMEOUT_MS,
} from "@/components/ads/config";
import {
  incrementInterstitialNavigationCount,
  markInterstitialShown,
  normalizeInternalHref,
  shouldShowInterstitial,
  trackSessionRouteVisit,
} from "@/lib/ads/interstitial-policy";

import InterstitialModal from "./InterstitialModal";

type InterstitialContextValue = {
  requestInterstitialNavigation: (_href: string) => boolean;
};

type NativeStatus = "idle" | "loading" | "filled" | "empty" | "unsupported";

declare global {
  interface Window {
    __BFC_INTERSTITIAL__?: {
      open: (_href: string) => boolean;
      debug: () => void;
      enabled: boolean;
      isOpen: boolean;
      nativeStatus: NativeStatus;
      pendingHref: string | null;
    };
  }
}

const InterstitialContext = createContext<InterstitialContextValue | null>(null);
const FALLBACK_SLOT_ID_PREFIX = "bfc-gam-interstitial-fallback-slot";

type GptSlot = {
  addService: (_service: unknown) => GptSlot;
  getOutOfPage?: () => boolean;
  getAdUnitPath?: () => string;
  getSlotElementId?: () => string;
  getResponseInformation?: () => unknown;
  setTargeting?: (_key: string, _value: string | string[]) => GptSlot;
  setConfig?: (_config: { targeting?: Record<string, string | string[]> }) => GptSlot;
};

type GptEvent = {
  slot: unknown;
  isEmpty?: boolean;
  size?: unknown;
  advertiserId?: unknown;
  campaignId?: unknown;
  creativeId?: unknown;
  lineItemId?: unknown;
  sourceAgnosticCreativeId?: unknown;
  sourceAgnosticLineItemId?: unknown;
};

type GptPubAds = {
  addEventListener: (_eventName: string, _listener: (_event: unknown) => void) => void;
  removeEventListener?: (_eventName: string, _listener: (_event: unknown) => void) => void;
  getSlots: () => unknown[];
};

type InterstitialGoogletag = {
  cmd: Array<() => void>;
  apiReady?: boolean;
  pubadsReady?: boolean;
  enums?: {
    OutOfPageFormat?: {
      INTERSTITIAL?: unknown;
    };
  };
  defineOutOfPageSlot?: (_adUnitPath: string, _formatOrDivId?: unknown) => GptSlot | null;
  defineSlot?: (_adUnitPath: string, _sizes: unknown[], _divId: string) => GptSlot | null;
  display: (_slotOrDivId: unknown) => void;
  destroySlots: (_slots?: unknown[]) => boolean;
  pubads: () => GptPubAds;
};

function ensureGoogletag() {
  window.googletag = window.googletag || { cmd: [] };
  return window.googletag as unknown as InterstitialGoogletag;
}

function asGptSlot(slot: unknown) {
  return slot as GptSlot;
}

function log(label: string, payload?: Record<string, unknown>) {
  if (payload) {
    console.log(`[BFC interstitial] ${label}`, payload);
    return;
  }

  console.log(`[BFC interstitial] ${label}`);
}

export function useInterstitialNavigation() {
  const value = useContext(InterstitialContext);

  if (!value) {
    return {
      requestInterstitialNavigation: () => false,
    } satisfies InterstitialContextValue;
  }

  return value;
}

export default function InterstitialProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? "/";

  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackSlotId, setFallbackSlotId] = useState(`${FALLBACK_SLOT_ID_PREFIX}-1`);
  const [nativeStatus, setNativeStatus] = useState<NativeStatus>("idle");

  const pendingHrefRef = useRef<string | null>(null);
  const nativeSlotRef = useRef<GptSlot | null>(null);
  const nativeListenersRef = useRef<Array<[string, (_event: unknown) => void]>>([]);
  const fallbackSlotRef = useRef<GptSlot | null>(null);
  const fallbackRequestIdRef = useRef(1);
  const fallbackTimeoutRef = useRef<number | null>(null);
  const isNavigatingRef = useRef(false);
  const nativeBootedRef = useRef(false);
  const nativeStatusRef = useRef<NativeStatus>("idle");

  const setNativeStatusSafe = useCallback((status: NativeStatus) => {
    nativeStatusRef.current = status;
    setNativeStatus(status);
  }, []);

  const clearFallbackTimeout = useCallback(() => {
    if (fallbackTimeoutRef.current !== null) {
      window.clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  }, []);

  const cleanupFallbackSlot = useCallback(() => {
    clearFallbackTimeout();

    const slot = fallbackSlotRef.current;
    fallbackSlotRef.current = null;

    if (slot && window.googletag?.cmd) {
      window.googletag.cmd.push(() => {
        try {
          (window.googletag as unknown as InterstitialGoogletag).destroySlots([slot]);
        } catch {}
      });
    }
  }, [clearFallbackTimeout]);

  const continueNavigation = useCallback(() => {
    if (isNavigatingRef.current) return;

    isNavigatingRef.current = true;

    const href = pendingHrefRef.current;
    pendingHrefRef.current = null;

    setFallbackOpen(false);
    cleanupFallbackSlot();

    if (href) {
      router.push(href);
      window.setTimeout(() => window.scrollTo(0, 0), 0);
    }

    window.setTimeout(() => {
      isNavigatingRef.current = false;
    }, 700);
  }, [cleanupFallbackSlot, router]);

  const debugNative = useCallback(() => {
    const googletag = window.googletag as unknown as InterstitialGoogletag | undefined;
    const pubads = googletag?.pubads?.() as GptPubAds | undefined;

    console.table({
      gptLoaded: Boolean(googletag),
      apiReady: Boolean(googletag?.apiReady),
      pubadsReady: Boolean(googletag?.pubadsReady),
      topWindow: window.top === window.self,
      localStorageWorks: (() => {
        try {
          const key = "__bfc_ls_test__";
          localStorage.setItem(key, "1");
          localStorage.removeItem(key);
          return true;
        } catch {
          return false;
        }
      })(),
      oopInterstitialEnum: String(googletag?.enums?.OutOfPageFormat?.INTERSTITIAL ?? "missing"),
      nativeStatus: nativeStatusRef.current,
      nativeSlotExists: Boolean(nativeSlotRef.current),
      fallbackSlotExists: Boolean(fallbackSlotRef.current),
      slots: pubads?.getSlots?.().length ?? 0,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    });

    console.log(
      "[BFC interstitial] slots",
      pubads?.getSlots?.().map((rawSlot: unknown) => {
        const slot = asGptSlot(rawSlot);

        return {
          adUnit: slot.getAdUnitPath?.(),
          elementId: slot.getSlotElementId?.(),
          outOfPage: slot.getOutOfPage?.(),
          response: slot.getResponseInformation?.(),
        };
      })
    );
  }, []);

  useEffect(() => {
    trackSessionRouteVisit(pathname);
  }, [pathname]);

  useEffect(() => {
    if (!GAM_ENABLED || !GAM_INTERSTITIAL_AD_UNIT || nativeBootedRef.current) return undefined;

    nativeBootedRef.current = true;
    setNativeStatusSafe("loading");

    const googletag = ensureGoogletag();
    let cancelled = false;

    googletag.cmd.push(() => {
      if (cancelled) return;

      const pubads = googletag.pubads();
      const outOfPageFormat = googletag.enums?.OutOfPageFormat?.INTERSTITIAL;

      if (!outOfPageFormat || window.top !== window.self) {
        log("native unsupported before define", {
          hasEnum: Boolean(outOfPageFormat),
          topWindow: window.top === window.self,
        });

        setNativeStatusSafe("unsupported");
        return;
      }

      const slot = googletag.defineOutOfPageSlot?.(GAM_INTERSTITIAL_AD_UNIT, outOfPageFormat);

      if (!slot) {
        log("native defineOutOfPageSlot returned null", {
          reason: "unsupported, duplicate OOP format, or GPT refused this environment",
          topWindow: window.top === window.self,
          existingSlots: pubads.getSlots().map((rawSlot: unknown) => {
            const existingSlot = asGptSlot(rawSlot);

            return {
              adUnit: existingSlot.getAdUnitPath?.(),
              elementId: existingSlot.getSlotElementId?.(),
              outOfPage: existingSlot.getOutOfPage?.(),
            };
          }),
        });

        setNativeStatusSafe("unsupported");
        return;
      }

      slot.setConfig?.({
        targeting: {
          placement: "interstitial_mobile_placement",
          placement_group: "interstitial_mobile_placement",
          inventory_tier: "premium",
          bfc_ad_mode: "native_oop_interstitial",
          native_candidate: "1",
          demand_formats: ["native", "fluid", "interstitial"],
        },
      });

      nativeSlotRef.current = slot.addService(pubads);

      const eventNames = [
        "slotRequested",
        "slotResponseReceived",
        "slotRenderEnded",
        "slotOnload",
        "impressionViewable",
      ];

      const makeListener = (eventName: string) => (rawEvent: unknown) => {
        const event = rawEvent as GptEvent;
        const eventSlot = asGptSlot(event.slot);

        if (event.slot !== nativeSlotRef.current) return;

        const response = eventSlot.getResponseInformation?.() ?? null;

        log(`native ${eventName}`, {
          isEmpty: event.isEmpty,
          size: event.size,
          response,
          advertiserId: event.advertiserId,
          campaignId: event.campaignId,
          creativeId: event.creativeId,
          lineItemId: event.lineItemId,
          sourceAgnosticCreativeId: event.sourceAgnosticCreativeId,
          sourceAgnosticLineItemId: event.sourceAgnosticLineItemId,
        });

        if (eventName === "slotRenderEnded") {
          setNativeStatusSafe(event.isEmpty ? "empty" : "filled");
        }
      };

      nativeListenersRef.current = eventNames.map((eventName) => {
        const listener = makeListener(eventName);
        pubads.addEventListener(eventName, listener);
        return [eventName, listener];
      });

      log("native out-of-page slot defined once", {
        adUnit: GAM_INTERSTITIAL_AD_UNIT,
        usingNativeInterstitial: true,
        outOfPage: nativeSlotRef.current.getOutOfPage?.(),
        note: "GPT will create its own container when this format fills.",
      });

      googletag.display(nativeSlotRef.current);
    });

    return () => {
      cancelled = true;
    };
  }, [setNativeStatusSafe]);

  useEffect(() => {
    if (!fallbackOpen) return undefined;

    let cancelled = false;
    const activeSlotId = fallbackSlotId;
    const googletag = ensureGoogletag();

    fallbackTimeoutRef.current = window.setTimeout(() => {
      if (cancelled) return;
      log("fallback timeout, continue navigation");
      continueNavigation();
    }, INTERSTITIAL_REQUEST_TIMEOUT_MS);

    googletag.cmd.push(() => {
      if (cancelled) return;

      const pubads = googletag.pubads();
      const sizes = [...GAM_INTERSTITIAL_FALLBACK_SIZES];
      const slot = googletag.defineSlot?.(GAM_INTERSTITIAL_FALLBACK_AD_UNIT, sizes, activeSlotId);

      if (!slot) {
        log("fallback defineSlot returned null", {
          adUnit: GAM_INTERSTITIAL_FALLBACK_AD_UNIT,
          activeSlotId,
        });

        continueNavigation();
        return;
      }

      slot.setConfig?.({
        targeting: {
          bfc_int_fallback: "1",
          placement: "interstitial_mobile_placement",
          placement_group: "interstitial_mobile_placement",
          inventory_tier: "premium_fallback",
          bfc_ad_mode: "interstitial_display_fallback",
          native_candidate: "1",
          demand_formats: ["native", "fluid", "display"],
        },
      });

      fallbackSlotRef.current = slot.addService(pubads);

      const onSlotRenderEnded = (rawEvent: unknown) => {
        const event = rawEvent as GptEvent;
        const eventSlot = asGptSlot(event.slot);

        if (event.slot !== fallbackSlotRef.current) return;

        pubads.removeEventListener?.("slotRenderEnded", onSlotRenderEnded);
        clearFallbackTimeout();

        log("fallback slotRenderEnded", {
          isEmpty: event.isEmpty,
          size: event.size,
          response: eventSlot.getResponseInformation?.() ?? null,
        });

        if (cancelled) return;
        if (event.isEmpty) continueNavigation();
      };

      pubads.addEventListener("slotRenderEnded", onSlotRenderEnded);

      log("fallback display request", {
        adUnit: GAM_INTERSTITIAL_FALLBACK_AD_UNIT,
        slotId: activeSlotId,
        sizes,
      });

      googletag.display(activeSlotId);
    });

    return () => {
      cancelled = true;
      clearFallbackTimeout();
    };
  }, [clearFallbackTimeout, continueNavigation, fallbackOpen, fallbackSlotId]);

  useEffect(() => {
    return () => {
      cleanupFallbackSlot();

      const nativeSlot = nativeSlotRef.current;
      const nativeListeners = nativeListenersRef.current;

      nativeSlotRef.current = null;
      nativeListenersRef.current = [];

      if (!window.googletag?.cmd) return;

      window.googletag.cmd.push(() => {
        const gt = window.googletag as unknown as InterstitialGoogletag;
        const pubads = gt.pubads();

        nativeListeners.forEach(([eventName, listener]) => {
          pubads.removeEventListener?.(eventName, listener);
        });

        if (nativeSlot) {
          try {
            gt.destroySlots([nativeSlot]);
          } catch {}
        }
      });
    };
  }, [cleanupFallbackSlot]);

  const requestInterstitialNavigation = useCallback(
    (rawHref: string) => {
      if (isNavigatingRef.current || fallbackOpen) return false;

      const href = normalizeInternalHref(rawHref);
      if (!href) return false;

      const count = incrementInterstitialNavigationCount();
      const eligibleByOwnPolicy =
        GAM_ENABLED &&
        Boolean(GAM_INTERSTITIAL_AD_UNIT) &&
        nativeStatusRef.current !== "filled" &&
        shouldShowInterstitial({
          navigationCount: count,
          minNavigations: INTERSTITIAL_MIN_NAVIGATIONS,
          repeatNavigations: INTERSTITIAL_REPEAT_NAVIGATIONS,
          cooldownMs: INTERSTITIAL_COOLDOWN_MS,
        });

      log("click eligibility", {
        href,
        count,
        eligibleByOwnPolicy,
        nativeStatus: nativeStatusRef.current,
      });

      if (!eligibleByOwnPolicy) return false;

      if (
        nativeStatusRef.current === "loading" ||
        nativeStatusRef.current === "filled" ||
        nativeStatusRef.current === "idle"
      ) {
        return false;
      }

      pendingHrefRef.current = href;
      fallbackRequestIdRef.current += 1;
      setFallbackSlotId(`${FALLBACK_SLOT_ID_PREFIX}-${fallbackRequestIdRef.current}`);
      cleanupFallbackSlot();
      markInterstitialShown();
      setFallbackOpen(true);
      return true;
    },
    [cleanupFallbackSlot, fallbackOpen]
  );

  useEffect(() => {
    window.__BFC_INTERSTITIAL__ = {
      open: requestInterstitialNavigation,
      debug: debugNative,
      enabled: GAM_ENABLED,
      isOpen: fallbackOpen,
      nativeStatus,
      pendingHref: pendingHrefRef.current,
    };

    return () => {
      delete window.__BFC_INTERSTITIAL__;
    };
  }, [debugNative, fallbackOpen, nativeStatus, requestInterstitialNavigation]);

  const value = useMemo(() => ({ requestInterstitialNavigation }), [requestInterstitialNavigation]);

  return (
    <InterstitialContext.Provider value={value}>
      <div data-bfc-interstitial-provider="" data-native-interstitial-status={nativeStatus}>
        {children}

        {GAM_ENABLED ? (
          <InterstitialModal
            slotId={fallbackSlotId}
            isVisible={fallbackOpen}
            onCloseAction={continueNavigation}
          />
        ) : null}
      </div>
    </InterstitialContext.Provider>
  );
}